// frontend/src/pages/Search/Search.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../contexts/SearchContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Search.css';

const Search = () => {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({
    type: [],
    grade: null,
    topic: null,
    sortBy: 'relevance'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [followStates, setFollowStates] = useState({});
  const [followLoading, setFollowLoading] = useState({});

  const { search, searchResults, loading, recentSearches, clearRecentSearches } = useSearch();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query || filters.type.length > 0) {
        performSearch();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, filters, currentPage]);

  const performSearch = async () => {
    try {
      setError(null);
      await search(query, {
        ...filters,
        page: currentPage,
        limit: 20
      });
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search. Please make sure the search index is initialized.');
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const handleQuickSearch = (searchTerm) => {
    setQuery(searchTerm);
  };

  const handleToggleFollow = async (userId, currentlyFollowing) => {
    if (!user) return;
    setFollowStates(prev => ({ ...prev, [userId]: !currentlyFollowing }));
    setFollowLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const endpoint = currentlyFollowing
        ? `/api/profile/unfollow/${userId}`
        : `/api/profile/follow/${userId}`;
      await api.post(endpoint);
    } catch (err) {
      console.error('Follow toggle error:', err);
      setFollowStates(prev => ({ ...prev, [userId]: currentlyFollowing }));
    } finally {
      setFollowLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const isFollowingUser = (userData) => {
    const uid = userData._id;
    if (followStates[uid] !== undefined) return followStates[uid];
    if (user && user.following && Array.isArray(user.following)) {
      return user.following.includes(uid);
    }
    return false;
  };

  const handleResultClick = (result) => {
    switch (result.type) {
      case 'user':
        navigate(`/profile/${result.data.username}`);
        break;
      case 'question':
        navigate(`/questions/${result.data._id}`);
        break;
      case 'worked_example':
        navigate(`/examples/${result.data._id}`);
        break;
      case 'post':
        if (result.data.user?.username) {
          navigate(`/profile/${result.data.user.username}`);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="search-page">
      <div className="search-header">
        <div className="search-bar-container">
          <div className="search-bar">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for questions, topics, users..."
              className="search-input"
            />
            {query && (
              <button className="clear-button" onClick={() => setQuery('')}>
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="search-body">
        <aside className="search-sidebar">
          <div className="filter-section">
            <h3>Content Type</h3>
            <div className="filter-checkboxes">
              {['post', 'question', 'worked_example', 'user'].map(type => (
                <label key={type}>
                  <input
                    type="checkbox"
                    checked={filters.type.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...filters.type, type]
                        : filters.type.filter(t => t !== type);
                      handleFilterChange('type', newTypes);
                    }}
                  />
                  <span>{type.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h3>Grade</h3>
            <select
              value={filters.grade || ''}
              onChange={(e) => handleFilterChange('grade', e.target.value || null)}
            >
              <option value="">All Grades</option>
              <option value="8">Grade 8</option>
              <option value="9">Grade 9</option>
              <option value="10">Grade 10</option>
              <option value="11">Grade 11</option>
              <option value="12">Grade 12</option>
            </select>
          </div>

          <div className="filter-section">
            <h3>Sort By</h3>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="relevance">Most Relevant</option>
              <option value="recent">Most Recent</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>

          {recentSearches.length > 0 && (
            <div className="filter-section">
              <h3>Recent Searches</h3>
              <ul className="recent-searches">
                {recentSearches.map((term, index) => (
                  <li key={index} onClick={() => handleQuickSearch(term)}>
                    {term}
                  </li>
                ))}
              </ul>
              <button onClick={clearRecentSearches} className="clear-recent">
                Clear all
              </button>
            </div>
          )}
        </aside>

        <main className="search-content">
          {error && (
            <div className="search-error" style={{
              padding: '20px', background: '#fee', color: '#c00',
              borderRadius: '8px', marginBottom: '20px'
            }}>
              <p>{error}</p>
            </div>
          )}

          {loading && (
            <div className="search-loading">
              <div className="spinner"></div>
              <p>Searching...</p>
            </div>
          )}

          {searchResults && !loading && (
            <>
              <div className="search-summary">
                <p>{searchResults.total || 0} results found</p>
              </div>

              <div className="search-results">
                {searchResults.results && searchResults.results.length > 0 ? (
                  searchResults.results.map((result, index) => (
                    <div key={index} className={`result-item result-${result.type}`}>

                      {result.type === 'user' && (
                        <div className="user-result-card">
                          <div
                            className="user-result-clickable"
                            onClick={() => navigate(`/profile/${result.data.username}`)}
                          >
                            <div className="user-result-avatar">
                              {result.data.profile?.avatar ? (
                                <img src={result.data.profile.avatar} alt={result.data.username} />
                              ) : (
                                <div className="user-avatar-placeholder">
                                  {result.data.profile?.name?.[0] || result.data.username?.[0]?.toUpperCase() || '?'}
                                </div>
                              )}
                            </div>
                            <div className="user-result-info">
                              <div className="user-result-name">
                                {result.data.profile?.name || result.data.username}
                              </div>
                              <div className="user-result-username">@{result.data.username}</div>
                              {result.data.profile?.bio && (
                                <div className="user-result-bio">
                                  {result.data.profile.bio.length > 100
                                    ? result.data.profile.bio.slice(0, 100) + '...'
                                    : result.data.profile.bio}
                                </div>
                              )}
                              <div className="user-result-meta">
                                <span className="grade-badge-sm">
                                  Grade {result.data.profile?.grade || '\u2014'}
                                </span>
                                <span className="user-result-stat">
                                  {result.data.followers?.length || 0} followers
                                </span>
                              </div>
                            </div>
                          </div>
                          {user && user._id !== result.data._id && (
                            <button
                              className={`search-follow-btn ${isFollowingUser(result.data) ? 'following' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFollow(result.data._id, isFollowingUser(result.data));
                              }}
                              disabled={followLoading[result.data._id]}
                            >
                              {followLoading[result.data._id]
                                ? '...'
                                : isFollowingUser(result.data) ? 'Following' : 'Follow'}
                            </button>
                          )}
                        </div>
                      )}

                      {result.type === 'post' && (
                        <div className="clickable-result" onClick={() => handleResultClick(result)}>
                          <div className="result-type">post</div>
                          <div className="result-content">
                            <p>{result.data.content}</p>
                            <div className="result-meta">
                              By{' '}
                              <span
                                className="username-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (result.data.user?.username) navigate(`/profile/${result.data.user.username}`);
                                }}
                              >
                                @{result.data.user?.username || 'unknown'}
                              </span>
                              {' \u2022 '}{result.data.likes?.length || 0} likes
                            </div>
                          </div>
                        </div>
                      )}

                      {result.type === 'question' && (
                        <div className="clickable-result" onClick={() => handleResultClick(result)}>
                          <div className="result-type">question</div>
                          <div className="result-content">
                            <h4>{result.data.question?.text || result.data.content || result.data.title}</h4>
                            <div className="result-meta">
                              Grade {result.data.grade} {'\u2022'} {result.data.topic}
                              {result.data.difficulty && ` \u2022 ${result.data.difficulty}`}
                              {result.data.createdBy?.username && (
                                <>
                                  {' \u2022 by '}
                                  <span
                                    className="username-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/profile/${result.data.createdBy.username}`);
                                    }}
                                  >
                                    @{result.data.createdBy.username}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {result.type === 'worked_example' && (
                        <div className="clickable-result" onClick={() => handleResultClick(result)}>
                          <div className="result-type">worked example</div>
                          <div className="result-content">
                            <h4>{result.data.title}</h4>
                            <div className="result-meta">
                              Grade {result.data.grade} {'\u2022'} {result.data.topic}
                              {result.data.createdBy?.username && (
                                <>
                                  {' \u2022 by '}
                                  <span
                                    className="username-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/profile/${result.data.createdBy.username}`);
                                    }}
                                  >
                                    @{result.data.createdBy.username}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    <p>No results found for "{query}"</p>
                  </div>
                )}
              </div>

              {searchResults.totalPages > 1 && (
                <div className="search-pagination">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <span>Page {currentPage} of {searchResults.totalPages}</span>
                  <button
                    disabled={currentPage === searchResults.totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && !searchResults && !error && (
            <div className="search-empty">
              <i className="fas fa-search"></i>
              <p>Start searching to find questions, posts, and users</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Search;
